import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const LandingRoot = styled.div`
  min-height: 100vh;
  background: #060818;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  overflow-x: hidden;
  font-family: 'Nunito', system-ui, -apple-system, sans-serif;

  * {
    box-sizing: border-box;
  }

  a {
    color: inherit;
  }
`;

export const AmbientLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(ellipse 90% 55% at 100% -10%, rgba(0, 212, 255, 0.12), transparent 50%),
    radial-gradient(ellipse 70% 45% at 0% 100%, rgba(242, 146, 5, 0.1), transparent 45%),
    linear-gradient(180deg, #0a0e27 0%, #060818 38%, #0a0e27 100%);
`;

export const ContentZ = styled.div`
  position: relative;
  z-index: 1;
`;

export const SiteHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(22px) saturate(165%);
  -webkit-backdrop-filter: blur(22px) saturate(165%);
  background: linear-gradient(180deg, rgba(12, 16, 42, 0.94) 0%, rgba(6, 8, 22, 0.86) 100%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 12px 40px rgba(0, 0, 0, 0.45);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(0, 212, 255, 0.35) 22%,
      rgba(242, 146, 5, 0.65) 50%,
      rgba(0, 212, 255, 0.35) 78%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

export const HeaderInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1240px;
  margin: 0 auto;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 12px;

  /* order in DOM: logo, nav, actions */
  & > *:nth-child(1) {
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
    min-width: 0;
  }

  & > *:nth-child(2) {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-self: stretch;
    width: 100%;
    min-width: 0;
  }

  & > *:nth-child(3) {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
    min-width: 0;
  }

  @media (min-width: 768px) {
    grid-template-columns: minmax(80px, 1fr) auto minmax(100px, 1fr);
    grid-template-rows: auto;
    column-gap: min(20px, 2vw);
    row-gap: 0;
    padding: 14px 22px 16px;

    & > *:nth-child(1) {
      grid-column: 1;
      grid-row: 1;
      justify-self: start;
    }

    & > *:nth-child(2) {
      grid-column: 2;
      grid-row: 1;
      justify-self: center;
      width: auto;
    }

    & > *:nth-child(3) {
      grid-column: 3;
      grid-row: 1;
      justify-self: end;
    }
  }

  @media (min-width: 1200px) {
    padding: 14px 28px 18px;
  }
`;

export const HeaderLogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none !important;
  transition: transform 0.25s ease, filter 0.25s ease;

  img {
    display: block;
    height: auto;
    width: auto;
    max-width: 172px;
    object-fit: contain;
    filter: drop-shadow(0 2px 12px rgba(0, 0, 0, 0.5));
  }

  @media (max-width: 767px) {
    img {
      max-height: 48px;
      max-width: 160px;
    }
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  &:hover img {
    filter: drop-shadow(0 4px 16px rgba(242, 146, 5, 0.25));
  }
`;

export const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 6px;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);

  @media (min-width: 768px) {
    gap: 6px 8px;
    padding: 4px 10px;
  }

  a {
    text-decoration: none !important;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.78) !important;
    padding: 8px 11px;
    border-radius: 999px;
    transition:
      color 0.2s ease,
      background 0.2s ease,
      box-shadow 0.2s ease;

    @media (min-width: 768px) {
      font-size: 15px;
      padding: 8px 14px;
    }

    &:hover {
      color: #fff !important;
      background: rgba(242, 146, 5, 0.18);
      box-shadow: 0 0 0 1px rgba(242, 146, 5, 0.25);
    }
  }
`;

export const HeaderActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  gap: 8px;
  width: max-content;
  max-width: min(100vw - 96px, 400px);

  @media (min-width: 768px) {
    gap: 12px;
    max-width: 420px;
  }

  & > a,
  & > span[role='button'] {
    width: 100%;
    min-width: 0;
    justify-content: center;
    text-align: center;
  }

  @media (max-width: 767px) {
    max-width: min(100vw - 72px, 400px);

    a,
    span[role='button'] {
      padding: 10px 12px !important;
      font-size: 13px !important;
      border-radius: 10px !important;
    }
  }
`;

export const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none !important;
  color: #e8e8ef !important;
  background: rgba(255, 255, 255, 0.04);
  transition: all 0.25s ease;
  cursor: pointer;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.18);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff !important;
  }
`;

export const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 26px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none !important;
  color: #fff !important;
  background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
  box-shadow: 0 4px 20px rgba(242, 146, 5, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(242, 146, 5, 0.45);
    color: #fff !important;
  }
`;

export const BtnOutlineAffirm = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  color: #e8e8ef !important;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

export const HeroSection = styled.section`
  position: relative;
  padding: 28px 16px 56px;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 480px) {
    padding: 36px 20px 64px;
  }

  @media (min-width: 992px) {
    padding: 72px 24px 96px;
    display: grid;
    grid-template-columns: 1fr 1.05fr;
    gap: 32px;
    align-items: center;
  }
`;

export const HeroBadge = styled.span`
  display: inline-block;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00d4ff;
  margin-bottom: 16px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 212, 255, 0.12);
  border: 1px solid rgba(0, 212, 255, 0.35);
`;

export const HeroTitle = styled.h1`
  font-size: clamp(1.65rem, 6.2vw, 3.35rem);
  line-height: 1.14;
  font-weight: 800;
  margin: 0 0 16px;
  color: #fff;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.35);

  @media (min-width: 992px) {
    margin-bottom: 20px;
  }

  span.accent {
    background: linear-gradient(90deg, #f29205, #ff9f43);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroLead = styled.p`
  font-size: clamp(1.02rem, 3.8vw, 1.28rem);
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 20px;
  max-width: 34rem;

  @media (min-width: 992px) {
    margin-bottom: 24px;
  }
`;

export const HeroCtas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;

  @media (max-width: 767px) {
    a {
      padding: 11px 20px !important;
      font-size: 14px !important;
    }
  }

  @media (min-width: 992px) {
    gap: 12px;
    margin-bottom: 28px;
  }
`;

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
  margin-bottom: 24px;
  max-width: 22rem;

  @media (min-width: 992px) {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 28px;
    max-width: none;
    margin-bottom: 0;
  }
`;

export const Stat = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-size: clamp(1.35rem, 4vw, 1.75rem);
    font-weight: 800;
    color: #f29205;
    line-height: 1.2;
  }
  span {
    font-size: clamp(13px, 3vw, 15px);
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.35;
  }

  @media (min-width: 992px) {
    min-width: 120px;

    strong {
      font-size: 1.75rem;
    }
    span {
      font-size: 15px;
    }
  }
`;

export const HeroVisual = styled.div`
  position: relative;
  margin-top: 20px;

  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

/** Notebook com screenshot na tela — perspectiva suave no desktop */
export const HeroDevice = styled.div`
  max-width: min(100%, 920px);
  margin: 0 auto;
  filter: drop-shadow(0 36px 64px rgba(0, 0, 0, 0.55))
    drop-shadow(0 16px 32px rgba(10, 14, 39, 0.5));

  @media (min-width: 992px) {
    transform: perspective(1500px) rotateY(-8deg) rotateX(4deg);
    transform-origin: 50% 50%;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition: transform 0.45s ease;
  }

  @media (min-width: 992px) {
    &:hover {
      transform: perspective(1500px) rotateY(-5deg) rotateX(2deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    &:hover {
      transform: none !important;
    }
  }
`;

export const HeroDeviceBezel = styled.div`
  padding: clamp(10px, 2.2vw, 14px) clamp(10px, 2.2vw, 14px) clamp(12px, 2.6vw, 17px);
  border-radius: clamp(11px, 1.8vw, 17px) clamp(11px, 1.8vw, 17px) 5px 5px;
  background: linear-gradient(
    148deg,
    #4a5160 0%,
    #2e3440 38%,
    #1a1e26 72%,
    #12151c 100%
  );
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.14),
    inset 0 -2px 6px rgba(0, 0, 0, 0.35);
`;

export const HeroDeviceScreen = styled.div`
  position: relative;
  border-radius: clamp(4px, 0.7vw, 7px);
  overflow: hidden;
  background: #050814;
  box-shadow:
    inset 0 0 0 1px rgba(0, 0, 0, 0.65),
    inset 0 0 20px rgba(0, 0, 0, 0.35);

  img {
    width: 100%;
    height: auto;
    display: block;
    vertical-align: bottom;
  }
`;

/** “Base” do laptop — faixa inferior */
export const HeroDeviceBase = styled.div`
  height: clamp(11px, 2vw, 14px);
  margin: 0 9% 0;
  border-radius: 0 0 11px 11px;
  background: linear-gradient(180deg, #2f3540 0%, #191c22 55%, #101318 100%);
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
`;

export const Section = styled.section`
  padding: 72px 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 88px 24px;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 48px;
`;

export const SectionKicker = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #f29205;
  margin-bottom: 10px;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.65rem, 3.4vw, 2.5rem);
  font-weight: 800;
  margin: 0 0 16px;
  color: #fff;
`;

export const SectionLead = styled.p`
  font-size: 1.22rem;
  line-height: 1.62;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
`;

export const AboutSection = styled.section`
  padding: 72px 20px;
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(0, 212, 255, 0.06) 0%, rgba(6, 8, 24, 0.4) 45%, transparent 100%);

  @media (min-width: 768px) {
    padding: 88px 24px;
  }
`;

export const AboutInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

export const AboutProse = styled.div`
  margin-top: 4px;

  p {
    margin: 0 0 1.35rem;
    font-size: 1.12rem;
    line-height: 1.74;
    color: rgba(255, 255, 255, 0.78);
    text-align: left;
  }

  p:last-child {
    margin-bottom: 0;
  }

  strong {
    color: rgba(255, 255, 255, 0.93);
    font-weight: 700;
  }
`;

export const FeatureGrid = styled.div`
  display: grid;
  gap: 18px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const FeatureCard = styled.div`
  padding: 22px 22px 24px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(22, 33, 62, 0.92) 0%, rgba(10, 14, 39, 0.95) 100%);
  border: 1px solid rgba(242, 146, 5, 0.22);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(242, 146, 5, 0.45);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }
`;

export const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  font-size: 24px;
  background: rgba(242, 146, 5, 0.15);
  color: #ffb347;
`;

export const FeatureText = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.58;
  color: rgba(255, 255, 255, 0.78);
`;

export const VideoSection = styled.section`
  padding: 72px 20px;
  background: rgba(0, 0, 0, 0.22);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

export const VideoInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

export const VideoFrame = styled.div`
  margin-top: 32px;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid rgba(242, 146, 5, 0.4);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
  aspect-ratio: 16 / 9;

  iframe {
    width: 100%;
    height: 100%;
    display: block;
    border: none;
  }
`;

export const TestimonialsGrid = styled.div`
  display: grid;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const QuoteCard = styled.blockquote`
  margin: 0;
  padding: 26px;
  border-radius: 16px;
  background: rgba(22, 33, 62, 0.55);
  border: 1px solid rgba(0, 212, 255, 0.2);
  text-align: left;
`;

export const QuoteText = styled.p`
  margin: 0 0 18px;
  font-size: 17px;
  line-height: 1.62;
  color: rgba(255, 255, 255, 0.82);
  font-style: normal;
`;

export const QuoteAuthor = styled.footer`
  font-weight: 700;
  color: #f29205;
  font-size: 17px;
`;

export const Stars = styled.div`
  margin-top: 8px;
  color: #f9a330;
  letter-spacing: 2px;
`;

export const SiteFooter = styled.footer`
  margin-top: 24px;
  padding: 0 18px max(88px, calc(env(safe-area-inset-bottom, 0px) + 72px));
  background: linear-gradient(185deg, #070b1a 0%, #030510 42%, #010208 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 -28px 64px rgba(0, 0, 0, 0.45);

  @media (min-width: 768px) {
    padding: 0 24px 48px;
  }
`;

export const FooterWrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding-top: 36px;

  @media (min-width: 768px) {
    padding-top: 48px;
  }
`;

export const FooterAccentLine = styled.div`
  height: 2px;
  width: 100%;
  max-width: min(280px, 85vw);
  margin: 0 auto 28px;
  border-radius: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 212, 255, 0.35) 25%,
    rgba(242, 146, 5, 0.75) 50%,
    rgba(0, 212, 255, 0.35) 75%,
    transparent
  );
  opacity: 0.9;

  @media (min-width: 768px) {
    margin-bottom: 40px;
  }
`;

export const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  max-width: 520px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: none;
    margin: 0;
    grid-template-columns: minmax(0, 1.08fr) minmax(0, 1fr);
    align-items: start;
    gap: 40px min(64px, 4vw);
  }
`;

export const FooterSupportCol = styled.div`
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
    justify-self: start;
  }
`;

export const FooterLegalCol = styled.div`
  text-align: center;
  margin-top: 28px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @media (min-width: 768px) {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    text-align: right;
    justify-self: end;
  }
`;

export const FooterTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #ffb84d;
`;

export const FooterMuted = styled.div`
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.58);
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (min-width: 768px) {
    font-size: 16px;
  }

  a {
    color: #7ae8ff;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s ease;

    &:hover {
      color: #a8f0ff;
      text-decoration: underline;
    }
  }
`;

